import { useRef } from 'react';
import SiteHeader from './components/SiteHeader';
import SiteFooter from './components/SiteFooter';
import { OUR_SERVICES } from './data/ourServices';
import useLandingMotion from './hooks/useLandingMotion';
import './styles/LandingPage.css';
import './styles/LandingMotion.css';
import './styles/ServicesPage.css';

function Arrow({ external = false }) {
  return (
    <svg width="19" height="19" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d={external ? 'M6 18 18 6M6 6h12v12' : 'M4 12h16m-6-6 6 6-6 6'} stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function ProductMark({ id }) {
  return (
    <span className={`mf-product-mark mf-product-mark-${id}`} aria-hidden="true">
      {id === 'united-ad' ? <><i /><i /><i /><i /></> : <svg width="26" height="26" viewBox="0 0 28 28" fill="none"><path d="M5 16V8h8M23 12v8h-8M8 20 20 8" stroke="currentColor" strokeWidth="4.5" strokeLinecap="square" strokeLinejoin="round" /></svg>}
    </span>
  );
}

function ServicePreview({ service }) {
  return (
    <figure className={`mf-product-preview${service.previewImage ? ' has-image' : ''}`}>
      <div className="mf-product-browser" aria-hidden="true">
        <span className="mf-browser-dots"><i /><i /><i /></span>
        <span>{new URL(service.url).hostname}</span>
        <Arrow external />
      </div>
      {service.previewImage ? (
        <img src={service.previewImage} alt={`${service.name} 서비스 홈페이지 화면`} loading="lazy" width="1440" height="1000" />
      ) : (
        <div className="mf-product-example" aria-label={`${service.name} 서비스 화면 예시`}>
          <div className="mf-example-sidebar">
            <span className="mf-example-brand"><ProductMark id={service.id} />{service.name}</span>
            <span className="mf-example-workspace">우리 팀 워크스페이스 <span aria-hidden="true">⌄</span></span>
            <span className="mf-example-label">WORKSPACE</span>
            {service.tags.map((tag, index) => <span className={`mf-example-menu${index === 0 ? ' is-current' : ''}`} key={tag}><i />{tag}</span>)}
          </div>
          <div className="mf-example-main">
            <div className="mf-example-topline"><span>워크스페이스</span><span>화면 예시</span></div>
            <div className="mf-example-body">
              <span className="mf-example-label">YOUR NEXT WORKFLOW</span>
              <strong>{service.summary}</strong>
              <p>팀의 다음 업무를 하나의 흐름으로 연결하세요.</p>
              <div className="mf-example-items">
                {service.workflow.map((step, index) => <div key={step}><span className="mf-example-step">0{index + 1}</span><span>{step}</span><Arrow /></div>)}
              </div>
              <div className="mf-example-team"><span><i>팀</i><i>원</i></span>함께하는 팀의 업무 공간</div>
            </div>
          </div>
        </div>
      )}
      <figcaption><span /><span>{service.previewImage ? '실제 서비스 홈페이지' : '서비스 화면 예시'}</span><span>{service.name}</span></figcaption>
    </figure>
  );
}

function ServiceSection({ service }) {
  return (
    <section id={service.id} className={`mf-service-section mf-service-${service.id}`} aria-labelledby={`${service.id}-title`}>
      <div className="mf-container">
        <div className="mf-service-topline" data-reveal><span><span>{service.number}</span>{service.category}</span><span>{service.eyebrow}</span></div>
        <div className="mf-service-intro-grid">
          <div className="mf-service-copy" data-reveal>
            <p className="mf-service-brand"><ProductMark id={service.id} />{service.name}<span>.</span></p>
            <h2 id={`${service.id}-title`}>{service.headline}</h2>
            <p className="mf-service-description">{service.description}</p>
            <div className="mf-service-tags" aria-label={`${service.name} 주요 기능`}>{service.tags.map((tag) => <span key={tag}>{tag}</span>)}</div>
            <a className="mf-button mf-service-button" href={service.url} target="_blank" rel="noopener noreferrer" aria-label={`${service.name} 서비스 바로가기 (새 탭)`}>서비스 바로가기 <Arrow external /></a>
            {service.domainNotice && <p className="mf-service-domain-notice">{service.domainNotice}</p>}
          </div>
          <div className="mf-service-visual" data-reveal="preview"><ServicePreview service={service} /></div>
        </div>
        <div className="mf-service-details" data-reveal>
          <div className="mf-service-features">
            {service.features.map((feature, index) => (
              <article key={feature.title}>
                <span className="mf-service-feature-number">0{index + 1}</span>
                <div><h3>{feature.title}</h3><p>{feature.description}</p></div>
              </article>
            ))}
          </div>
          <div className="mf-service-workflow">
            <span className="mf-service-small-label">CONNECTED WORKFLOW</span>
            <h3>{service.summary}</h3>
            <ol>{service.workflow.map((step, index) => <li key={step}><span className="mf-workflow-number">0{index + 1}</span><span>{step}</span>{index < service.workflow.length - 1 && <span className="mf-workflow-arrow" aria-hidden="true">→</span>}</li>)}</ol>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function ServicesPage({ onNavigate }) {
  const landingRef = useRef(null);
  useLandingMotion(landingRef);

  const scrollToService = (event) => {
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const section = document.getElementById(event.currentTarget.hash.slice(1));
    if (!section) return;
    event.preventDefault();
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}${event.currentTarget.hash}`);
    section.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
  };

  return (
    <div className="marketfit-landing mf-services-page" ref={landingRef}>
      <a className="mf-skip" href="#main-content">본문 바로가기</a>
      <SiteHeader isServicesPage onNavigate={onNavigate} />
      <main id="main-content" tabIndex={-1}>
        <section className="mf-services-hero" aria-labelledby="services-title">
          <div className="mf-container">
            <span className="mf-services-eyebrow" data-reveal><i />OUR SERVICES</span>
            <h1 id="services-title" data-reveal>비즈니스의 다음 단계,<br /><em>우리의 서비스와 함께.</em></h1>
            <p className="mf-services-hero-description" data-reveal>광고와 판매부터 생산과 유통까지.<br />우리 팀에 필요한 서비스를 만나보세요.</p>
            <nav className="mf-services-shortcuts" aria-label="서비스 소개 바로가기" data-reveal>
              {OUR_SERVICES.map((service) => <a href={`#${service.id}`} className={`mf-service-shortcut mf-shortcut-${service.id}`} key={service.id} onClick={scrollToService}><ProductMark id={service.id} /><span><strong>{service.name}</strong><span>{service.category}</span></span><span className="mf-shortcut-arrow" aria-hidden="true">↓</span></a>)}
            </nav>
            <div className="mf-services-hero-bottom" aria-hidden="true"><span>TWO SERVICES. MORE POSSIBILITIES.</span><span>서비스 살펴보기 <span>↓</span></span></div>
          </div>
        </section>
        <div id="service-list">{OUR_SERVICES.map((service) => <ServiceSection service={service} key={service.id} />)}</div>
        <section className="mf-services-closing" aria-labelledby="services-closing-title">
          <div className="mf-container" data-reveal>
            <span className="mf-eyebrow">MAKE YOUR NEXT MOVE.</span>
            <h2 id="services-closing-title">우리 팀의 다음 일을,<br />더 자연스럽게 연결하세요.</h2>
            <p>필요한 서비스에서 새로운 업무의 흐름을 시작해 보세요.</p>
            <div className="mf-services-closing-links">{OUR_SERVICES.map((service) => <a href={service.url} target="_blank" rel="noopener noreferrer" key={service.id} aria-label={`${service.name} 시작하기 (새 탭)`}>{service.name} <Arrow external /></a>)}</div>
          </div>
        </section>
      </main>
      <SiteFooter onNavigate={onNavigate} />
    </div>
  );
}
